import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import LinkIcon from '@mui/icons-material/Link'
import SearchIcon from '@mui/icons-material/Search'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FormDefinition,
  useCreateForm,
  useDeleteForm,
  useFormList,
} from 'api/aws/form-builder/useFormBuilder'
import FormEditor from './FormEditor'

// ── Status colors ────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'default' | 'error'> = {
  active: 'success',
  draft: 'warning',
  archived: 'default',
}

// ── Component ────────────────────────────────────────────────────────────────

const FormBuilderPage = () => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const navigate = useNavigate()
  const { showSnackbar } = useSnackbarContext()

  const { data: forms = [], isLoading } = useFormList()
  const createForm = useCreateForm()
  const deleteForm = useDeleteForm()

  const [search, setSearch] = useState('')
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingFormId, setEditingFormId] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<FormDefinition | null>(null)
  const [submissionsFormId, setSubmissionsFormId] = useState<string | null>(null)

  // ── Filtered list ────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    if (!search.trim()) return forms
    const q = search.toLowerCase()
    return forms.filter(
      f =>
        f.name?.toLowerCase().includes(q) ||
        f.description?.toLowerCase().includes(q) ||
        f.status?.toLowerCase().includes(q)
    )
  }, [forms, search])

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleNewForm = useCallback(() => {
    setEditingFormId(null)
    setEditorOpen(true)
  }, [])

  const handleEdit = useCallback((form: FormDefinition) => {
    setEditingFormId(form.formId)
    setEditorOpen(true)
  }, [])

  const handleDuplicate = useCallback(
    async (form: FormDefinition) => {
      try {
        await createForm.mutateAsync({
          name: `${form.name} (copy)`,
          description: form.description,
          fields: form.fields,
          clickUpTarget: form.clickUpTarget,
        })
        showSnackbar('Form duplicated', 'success')
      } catch {
        showSnackbar('Failed to duplicate form', 'error')
      }
    },
    [createForm, showSnackbar]
  )

  const handleDelete = useCallback(async () => {
    if (!deleteConfirm) return
    try {
      await deleteForm.mutateAsync(deleteConfirm.formId)
      showSnackbar('Form deleted', 'success')
      setDeleteConfirm(null)
    } catch {
      showSnackbar('Failed to delete form', 'error')
    }
  }, [deleteConfirm, deleteForm, showSnackbar])

  const handleCopyLink = useCallback(
    (form: FormDefinition) => {
      const url = `${window.location.origin}/forms/${form.formId}/submit`
      navigator.clipboard.writeText(url)
      showSnackbar('Share link copied to clipboard', 'success')
    },
    [showSnackbar]
  )

  const handleViewSubmissions = useCallback(
    (form: FormDefinition) => {
      setSubmissionsFormId(form.formId)
    },
    []
  )

  // ── Render ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto', paddingTop: pinned ? '60px' : 0, transition: 'padding-top 0.3s ease' }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>
          Form Builder
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewForm}>
          New Form
        </Button>
      </Stack>

      {/* Search */}
      <TextField
        size="small"
        placeholder="Search forms..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ mb: 2, width: 340 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">
                Fields
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">
                Submissions
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">
                    {search ? 'No forms match your search' : 'No forms yet. Create your first form!'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map(form => (
                <TableRow
                  key={form.formId}
                  hover
                  sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
                  onClick={() => handleEdit(form)}
                >
                  <TableCell>
                    <Typography fontWeight={500}>{form.name}</Typography>
                    {form.description && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: 'block' }}>
                        {form.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={form.status}
                      size="small"
                      color={STATUS_COLORS[form.status] || 'default'}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell align="center">{form.fields?.length ?? 0}</TableCell>
                  <TableCell align="center">{form.submitCount ?? 0}</TableCell>
                  <TableCell>
                    {form.createdAt
                      ? new Date(form.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : '—'}
                  </TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(form)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton size="small" onClick={() => handleDuplicate(form)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy share link">
                        <IconButton size="small" onClick={() => handleCopyLink(form)}>
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View submissions">
                        <IconButton size="small" onClick={() => handleViewSubmissions(form)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteConfirm(form)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Form Editor Dialog */}
      {editorOpen && (
        <FormEditor
          formId={editingFormId}
          open={editorOpen}
          onClose={() => {
            setEditorOpen(false)
            setEditingFormId(null)
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Form</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>? This will also
            delete all submissions. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submissions Viewer Dialog */}
      {submissionsFormId && (
        <SubmissionsDialog formId={submissionsFormId} onClose={() => setSubmissionsFormId(null)} />
      )}
    </Box>
  )
}

// ── Submissions Dialog ───────────────────────────────────────────────────────

import { useForm as useFormQuery, useFormSubmissions, FormSubmission } from 'api/aws/form-builder/useFormBuilder'

const SubmissionsDialog = ({ formId, onClose }: { formId: string; onClose: () => void }) => {
  const { data: form } = useFormQuery(formId)
  const { data: submissions = [], isLoading } = useFormSubmissions(formId)

  return (
    <Dialog open maxWidth="md" fullWidth onClose={onClose}>
      <DialogTitle>Submissions — {form?.name || 'Loading...'}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : submissions.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No submissions yet
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Submitted</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>By</TableCell>
                  {form?.fields?.map(f => (
                    <TableCell key={f.fieldId} sx={{ fontWeight: 600 }}>
                      {f.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {submissions.map((sub: FormSubmission) => (
                  <TableRow key={sub.submissionId}>
                    <TableCell>
                      {sub.submittedAt
                        ? new Date(sub.submittedAt).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })
                        : '—'}
                    </TableCell>
                    <TableCell>{sub.submittedBy || '—'}</TableCell>
                    {form?.fields?.map(f => (
                      <TableCell key={f.fieldId}>
                        {Array.isArray(sub.responses?.[f.fieldId])
                          ? sub.responses[f.fieldId].join(', ')
                          : sub.responses?.[f.fieldId]?.toString() ?? '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default FormBuilderPage
